import { Input, Switch, Tooltip } from '@trackman/web-shared-components';
import NoAvailableGames from 'components/noData/NoAvailableGames';
import SimpleTable from 'components/table/SimpleTable';
import { IPreferences } from 'constants/IPreferences';
import dayjs from 'dayjs';
import { History } from 'history';
import useLocalStorage from 'hooks/useLocalStorage/useLocalStorage';
import { ReactComponent as ResetIcon } from 'images/resetIcon.svg';
import { ReactComponent as RightArrow } from 'images/rightArrow.svg';
import { useStores } from 'index';
import { observer } from 'mobx-react-lite';
import React, { useEffect, useMemo, useState } from 'react';
import { defaultPreferences } from '../../hooks/useLocalStorage/defaultPreferences';
import { ArrowWrapper, CellContent, StyledButton, StyledCheckbox, StyledPaper, TableMenu } from './styles/HomeStyles';
import { ReactComponent as CheckmarkIcon } from 'images/checkmarkIcon.svg';

interface IProps {
  history: History;
}
const Home: React.FC<IProps> = observer((props) => {
  const broadcastService = useStores().broadcastService;
  const { history } = props;
  const sessionStore = useStores().sessionStore;
  const [preferences, setPreferences] = useLocalStorage<IPreferences>('preferences', defaultPreferences);
  const gameStore = useStores().gameStore;

  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(10);
  const [count, setCount] = useState(0);

  const timeDisplay = (time: string): string => {
    const timeWithoutOffset = dayjs(time.split('.')[0]);

    return `${timeWithoutOffset.format('MM/DD')} @ ${timeWithoutOffset.format('hh:mm A')}`;
  };
  const wrapTextCell = (text: string): JSX.Element => {
    return <CellContent>{text}</CellContent>;
  };
  const renderWatchButton = (id: string): JSX.Element => {
    const game = sessionStore.sessions.find((session) => session.id === id);

    return (
      <ArrowWrapper
        data-testid={`watch-button-${sessionStore.sessions.indexOf(game)}`}
        onClick={(): void => {
          if (game) {
            gameStore.setGameManually(game);
          }
          history.push(
            `/Metrics/${id}?isScheduled=${game.isScheduledGame}&sharingNetworkRules=${sessionStore.useSharingSharingNetworkRules}`
          );
        }}
      >
        <RightArrow />
      </ArrowWrapper>
    );
  };
  const renderRefreshButton = (): JSX.Element => {
    return (
      <StyledButton
        intent='primary'
        label='Refresh'
        data-testid={`refresh-button`}
        onClick={(): void => {
          sessionStore.fetchData(sessionStore.useSharingSharingNetworkRules);
        }}
        startIcon={<ResetIcon />}
      />
    );
  };
  const renderVerified = (isVerified: boolean): JSX.Element | null => {
    return isVerified ? <CheckmarkIcon /> : null;
  };

  const handleOnChange = (checked: boolean): void => {
    sessionStore.setUseSharingSharingNetworkRules(checked);
    sessionStore.fetchData(checked);
  };

  const setFiltersByUserPreferences = (): void => {
    for (const [key, value] of Object.entries(preferences)) {
      if (key !== 'sort' && value.value !== '') {
        sessionStore.addFilter({ ...value, onRemove: (i) => sessionStore.removeFilter(i), pinned: true });
      }
      if (key === 'sort') {
        sessionStore.changeSortOrderBy(value.value);
      }
    }
  };

  const renderCellContent = (text: string) => {
    return (
      <Tooltip title={text} placement='bottom' arrow>
        <CellContent maxWidth='150px'>{text}</CellContent>
      </Tooltip>
    );
  };

  useEffect(() => {
    broadcastService.stop();
    sessionStore.fetchData(sessionStore.useSharingSharingNetworkRules).then(() => setFiltersByUserPreferences());
  }, []);

  const columns = React.useMemo(
    () => [
      {
        Header: 'Time',
        accessor: 'gameDate',
        filterable: false,
        width: 160,
        Cell: (cellInfo: any, accessor: any) => timeDisplay(cellInfo[accessor]),
      },
      {
        Header: 'Home Team',
        sortable: true,
        accessor: 'homeTeam',
        Cell: (cellInfo: any, accessor: any) => renderCellContent(cellInfo[accessor]),
      },
      {
        Header: 'Away Team',
        sortable: true,
        accessor: 'awayTeam',
        Cell: (cellInfo: any, accessor: any) => renderCellContent(cellInfo[accessor]),
      },
      {
        Header: 'Venue',
        sortable: true,
        accessor: 'venue',
        Cell: (cellInfo: any, accessor: any) => renderCellContent(cellInfo[accessor]),
      },
      {
        Header: 'Field',
        sortable: true,
        accessor: 'field',
        Cell: (cellInfo: any, accessor: any) => renderCellContent(cellInfo[accessor]),
      },
      {
        Header: 'Type',
        accessor: 'sessionType',
        filterable: false,
        width: 90,
      },
      {
        Header: 'Private',
        accessor: 'privateSession',
        width: 90,
        filterable: false,
        Cell: (cellInfo: any, accessor: any) => renderVerified(cellInfo[accessor]),
      },
      {
        Header: '',
        accessor: 'id',
        width: 90,
        filterable: false,
        Cell: (cellInfo: any, accessor: any) => renderWatchButton(cellInfo[accessor]),
      },
    ],
    [sessionStore.useSharingSharingNetworkRules]
  );

  const renderNoDataComponents = (): JSX.Element => {
    return <NoAvailableGames button={renderRefreshButton()} organizationName={sessionStore.organization.name} />;
  };

  const [search, setSearch] = useState('');

  const searchHandler: React.ChangeEventHandler<HTMLTextAreaElement | HTMLInputElement> = (event) => {
    setSearch(event.target.value);
  };

  const dataMemorized = useMemo(() => {
    setCount(sessionStore.sessions.length);

    const filteredSessions = sessionStore.sessions
      .filter(
        (session) =>
          session.homeTeam.toLowerCase().includes(search.toLowerCase()) ||
          session.awayTeam.toLowerCase().includes(search.toLowerCase()) ||
          session.venue.toLowerCase().includes(search.toLowerCase()) ||
          session.field.toLowerCase().includes(search.toLowerCase()) ||
          session.sessionType.toLowerCase().includes(search.toLowerCase())
      )
      .slice(page * rowsPerPage, (page + 1) * rowsPerPage);

    return filteredSessions;
  }, [sessionStore.sessions, sessionStore.sort, search, page, rowsPerPage, sessionStore.useSharingSharingNetworkRules]);

  const onRowsPerPageChangeHandler: React.ChangeEventHandler<HTMLTextAreaElement | HTMLInputElement> = (event) => {
    setRowsPerPage(event.target.value as any);
  };

  const onChangePage = (event: React.MouseEvent<HTMLButtonElement, MouseEvent> | null, newPage: number): void => {
    setPage(newPage);
  };
  useEffect(() => {
    setPage(0);
  }, [rowsPerPage]);
  return (
    <div data-testid='find-game-section'>
      <TableMenu data-testid='table-menu'>
        <Input
          value={search}
          placeholder='Search...'
          onChange={searchHandler}
          width={312}
          data-testid='stadium-filter'
          inputProps={{ 'data-testid': 'session-filter' }}
        />
        <Switch
          checked={sessionStore.useSharingSharingNetworkRules}
          onChange={(_, checked: boolean): void => handleOnChange(checked)}
          label='Include shared games'
          data-testid='shared-games-switch'
        />
      </TableMenu>
      <StyledPaper>
        {/* @ts-ignore */}
        <SimpleTable
          columns={columns}
          data={dataMemorized}
          onHeaderClick={(columnKey: string): void => sessionStore.changeSortOrderBy(columnKey)}
          sort={sessionStore.sort}
          isLoading={sessionStore.isLoading}
          noDataComponent={renderNoDataComponents()}
          withFooter={count > 0}
          page={page}
          onRowsPerPageChange={onRowsPerPageChangeHandler}
          rowsPerPage={rowsPerPage}
          count={count}
          onChangePage={onChangePage}
        />
      </StyledPaper>
    </div>
  );
});

export default Home;
